import { useState, useEffect, useMemo, useRef } from 'react';
import { LayoutGrid, List, Clock, RefreshCw, Download } from 'lucide-react';
import { useProducts } from '../contexts/ProductContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import * as historyService from '../services/historyService';
import type { ProcessingHistoryEntry } from '../services/historyService';
import type { CartItem } from '@jigflow/shared';
import ProductModal from '../components/ProductModal';
import ProcessingModal from '../components/ProcessingModal';

type TabId = 'process' | 'history';

export function DashboardPage() {
  const { products, categories, loading: productsLoading, refreshProducts } = useProducts();
  const { workspace } = useWorkspace();
  const { checkout, isAuthenticated } = useAuth();
  const planCheckoutTriggered = useRef(false);

  // Auto-trigger checkout when arriving from marketing site with ?plan= param
  useEffect(() => {
    if (planCheckoutTriggered.current || !isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');
    if (plan && ['starter', 'pro', 'business'].includes(plan)) {
      planCheckoutTriggered.current = true;
      // Clean the URL so refreshing doesn't re-trigger
      window.history.replaceState({}, '', '/dashboard');
      checkout(plan);
    }
  }, [isAuthenticated, checkout]);

  const [activeTab, setActiveTab] = useState<TabId>('process');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [processingItem, setProcessingItem] = useState<CartItem | null>(null);
  const [isProcessingOpen, setIsProcessingOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // History state
  const [history, setHistory] = useState<ProcessingHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const filteredProducts = useMemo(() => {
    if (selectedCategory === 'all') return products;
    return products.filter(p => p.category === selectedCategory);
  }, [products, selectedCategory, categories]);

  const loadHistory = async () => {
    if (!workspace?.id) return;
    setHistoryLoading(true);
    try {
      const data = await historyService.getHistory(workspace.id);
      setHistory(data);
    } catch (err) {
      console.error('[Dashboard] Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, workspace?.id]);

  const handleProductClick = (product: any) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleProcess = (item: CartItem) => {
    setIsModalOpen(false);
    setProcessingItem(item);
    setIsProcessingOpen(true);
  };

  const handleProcessComplete = () => {
    setProcessingItem(null);
    setIsProcessingOpen(false);
    setSelectedProduct(null);
    // Refresh history if on history tab
    if (activeTab === 'history') loadHistory();
  };

  const handleRefresh = () => {
    refreshProducts();
    if (activeTab === 'history') loadHistory();
  };

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'process', label: 'Process', icon: <LayoutGrid size={16} /> },
    { id: 'history', label: 'History', icon: <Clock size={16} /> },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Process customizations and generate files
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 transition"
          title="Refresh"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Process Tab */}
      {activeTab === 'process' && (
        <div className="space-y-6">
          {/* Controls */}
          <div className="flex items-center justify-between">
            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  selectedCategory === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                All Products
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    selectedCategory === cat.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Grid view"
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition ${
                  viewMode === 'list'
                    ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="List view"
              >
                <List size={16} />
              </button>
            </div>
          </div>

          {/* Product Grid */}
          {productsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredProducts.map(product => (
                <button
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg hover:border-blue-400 dark:hover:border-blue-500 transition group text-left"
                >
                  <div className="aspect-square bg-gray-50 dark:bg-gray-900 overflow-hidden">
                    <img
                      src={product.image || 'https://placehold.co/300x300?text=No+Image'}
                      alt={product.name}
                      className="w-full h-full object-contain p-2 group-hover:scale-105 transition"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-800 dark:text-white text-sm truncate">
                      {product.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {product.description || 'No description'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredProducts.map(product => (
                <button
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                  className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg hover:border-blue-400 transition group text-left flex items-center gap-4 p-3"
                >
                  <div className="w-16 h-16 bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={product.image || 'https://placehold.co/300x300?text=No+Image'}
                      alt={product.name}
                      className="w-full h-full object-contain p-1 group-hover:scale-105 transition"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-800 dark:text-white truncate">{product.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                      {product.description || 'No description'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {filteredProducts.length === 0 && !productsLoading && (
            <div className="text-center py-12 text-gray-400">
              <p className="text-lg">No products found</p>
              <p className="text-sm mt-1">Add products in Settings &rarr; Products</p>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Clock size={48} className="mx-auto mb-3 opacity-50" />
              <p className="text-lg">No processing history yet</p>
              <p className="text-sm mt-1">Your downloads will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Product</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Format</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Date</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {history.map(entry => (
                    <tr key={entry.id} className="bg-white dark:bg-gray-900">
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium">{entry.productName}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 uppercase">
                          {entry.format}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {entry.filePath && (
                          <button
                            onClick={async () => {
                              try {
                                const url = historyService.getDownloadUrl(entry.filePath!);
                                const res = await fetch(url);
                                const blob = await res.blob();
                                const blobUrl = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = blobUrl;
                                a.download = `${entry.productName}.${entry.format}`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(blobUrl);
                              } catch (err) {
                                console.error('[Dashboard] Re-download failed:', err);
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition"
                            title="Re-download"
                          >
                            <Download size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProduct(null);
        }}
        onProcess={handleProcess}
      />

      {processingItem && (
        <ProcessingModal
          isOpen={isProcessingOpen}
          onClose={() => {
            setIsProcessingOpen(false);
            setProcessingItem(null);
          }}
          item={processingItem}
          onComplete={handleProcessComplete}
        />
      )}
    </div>
  );
}
